import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { FormattedMessage } from 'react-intl'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'
import {
  selectPricingConfig,
  getPlanWithLowestMonthlyPrice,
  selectCoupon,
} from 'js/store/pricingSlice'
import useLocale from 'js/hooks/useLocale'

import ChooseSubscriptionProduct from './ChooseSubscriptionProduct'
import BestPlanInfo from './BestPlanInfo'
import { mergePricingConfigs } from 'js/utils/mergePricingConfigs'
import LSButton from '../../LSButton'

function ChooseSubscription({
  onSelectSubscription,
  planToSelect,
  include24Months = false,
}) {
  const pricingConfig = useSelector(selectPricingConfig())
  const regularPricingConfig = useSelector(selectPricingConfig(null))
  const coupon = useSelector(selectCoupon)
  const [selectedProduct, setSelectedProduct] = useState({})
  const { country } = useLocale()

  useEffect(() => {
    if (!pricingConfig || !regularPricingConfig) return
    if (pricingConfig.products.length < 1) return

    const filteredProducts = mergePricingConfigs(
      pricingConfig,
      regularPricingConfig,
    ).filter((product) => {
      if (product.discount === 100) return true
      if (country === 'US' && product.duration === 1) return false
      if (country !== 'US' && product.duration === 6) return false
      if (!include24Months && product.duration === 24) return false
      return true
    })

    // Select the best plan (lowest monthly price)
    const preSelectedProduct = getPlanWithLowestMonthlyPrice(filteredProducts)

    setSelectedProduct(preSelectedProduct)
  }, [pricingConfig, regularPricingConfig, country, include24Months])

  const handleSelectSubscription = (product) => {
    onSelectSubscription(product)
    pushToDataLayer({
      event: 'signup_form',
      section: 'select_subscription',
      label: selectedProduct,
      success: '1', //send 1 if the select subscription is successfull and the user proceeds to the next step successfully, 0 if any error occurs
      error_message: undefined, //if an error occurs when the user selects a subscription plan and proceeds to the next step send error messages, if no error send undefined
    })
  }

  const products = mergePricingConfigs(
    pricingConfig,
    regularPricingConfig,
  ).filter((product) => {
    if (product.discount === 100) return true
    if (country === 'US' && product.duration === 1) return false
    if (country !== 'US' && product.duration === 6) return false
    if (!include24Months && product.duration === 24) return false
    return true
  })

  if (!regularPricingConfig) return null

  const currency = pricingConfig.currency
  const bestPlan = getPlanWithLowestMonthlyPrice(products)

  if (planToSelect) {
    return onSelectSubscription(planToSelect)
  }

  return (
    <div className="px-18 lg:px-0">
      <div className="mx-auto max-w-480 px-10 pt-18 sm:px-28 lg:max-w-none lg:px-0">
        <div
          className={cn(
            `flex flex-col-reverse lg:grid lg:grid-cols-${products.length} justify-center gap-0 lg:gap-2`,
            'mx-auto mb-24 max-w-1024 items-center text-center',
            'rounded-12',
            products.length > 3 ? 'lg:gap-4' : 'lg:gap-2',
          )}
          data-testid="choose-subscription"
        >
          {products.map((product) => {
            return (
              <ChooseSubscriptionProduct
                key={product.duration}
                {...product}
                currency={currency}
                selected={product.duration === selectedProduct?.duration}
                onClick={() => {
                  setSelectedProduct(product)
                }}
                isFallbackPrice={coupon && !product.discount}
                bestPlanInfo={
                  bestPlan.duration === product.duration ? (
                    <BestPlanInfo />
                  ) : null
                }
                trialDuration={product.trialDuration}
                onSelectProduct={() => handleSelectSubscription(product)}
              />
            )
          })}
        </div>
      </div>
      {selectedProduct?.duration && (
        <LSButton
          link={false}
          variant="tertiary"
          size="large"
          upperCase
          onClick={() => handleSelectSubscription(selectedProduct)}
          data-testid="choose-button-mobile"
        >
          <FormattedMessage id="lets_begin" defaultMessage="Let's Begin" />
        </LSButton>
      )}
    </div>
  )
}

ChooseSubscription.propTypes = {
  onSelectSubscription: PropTypes.func,
  planToSelect: PropTypes.number,
  include24Months: PropTypes.bool,
}

export default ChooseSubscription
